import { safeDisplayUrl } from '../safeDisplayUrl';
import { useEffect, useRef, useState } from "react";
import type { JsonObject } from "@inf-monkeys-tech/monkeys";

/** Explicit copy for public record values; credentials use the ephemeral result host. */
export function RecordDetailCopyValue({ value, copy, translate, valueFormat = 'text' }: { value: string; copy: JsonObject; valueFormat?: 'text' | 'absolute-url'; translate: (key: string) => string }) {
  const [feedback, setFeedback] = useState<"success" | "failure" | null>(null);
  const mounted = useRef(true);
  const pending = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  return <span className="flex min-w-0 flex-col items-start gap-2">
    <span className="whitespace-pre-wrap break-all">{value}</span>
    <button type="button" className="rounded border border-border px-2 py-1 text-sm text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={async () => {
        if (pending.current) return;
        pending.current = true;
        setFeedback(null);
        try {
          if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable.");
          let copied = value;
          if (valueFormat === 'absolute-url') {
            const safe = safeDisplayUrl(value, 'link');
            if (!safe) throw new Error('Unsafe copy URL.');
            const absolute = new URL(safe, window.location.origin);
            if (!['http:', 'https:'].includes(absolute.protocol) || !safeDisplayUrl(absolute.href, 'link')) throw new Error('Unsafe copy URL.');
            copied = absolute.href;
          } else if (valueFormat !== 'text') throw new Error('Unknown copy format.');
          await navigator.clipboard.writeText(copied);
          if (mounted.current) setFeedback("success");
        } catch {
          if (mounted.current) setFeedback("failure");
        } finally { pending.current = false; }
      }}>{translate(String(copy.labelKey))}</button>
    {feedback ? <span role={feedback === "failure" ? "alert" : "status"}>{translate(String(feedback === "success" ? copy.successLabelKey : copy.failureLabelKey))}</span> : null}
  </span>;
}
